
import { getPositionWithFallback, useLocationPermissionSync } from "../../../../utils/getPositionWithFallback";
import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { GoogleMap, Marker, Circle, useJsApiLoader } from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";
import CustomButton from "../../../../components/CustomButton";
import CustomText, { textStyles } from "../../../../components/CustomText";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import profilecross from "../../../../assets/profilecross.svg";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown";
import LocationPermissionModal from "../../../../components/PermissionModal";
import { convertLocalDateTimeToUTC } from "../../../../components/dateTimeUtils";

const mapContainerStyle = {
  width: "100%",
  height: "250px",
  borderRadius: "8px",
};

const fullDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const AddScheduleDrawer = ({ open, onClose, onAddSuccess }) => {
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;
  const { t, i18n } = useTranslation();
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;

const [hasCheckedPermission, setHasCheckedPermission] = useState(false);
const isOnline = () => navigator.onLine;

  const initialForm = {
    startTime: "",
    endTime: "",
    graceMinutes: "",
    breakMinutes: "",
    workingDays: [],
    locationName: "",
    radiusMeters: 100,
    worker: "",
  };

  const [form, setForm] = useState(initialForm);
  const [workers, setWorkers] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [workerPage, setWorkerPage] = useState(1);
  const [workerLimit] = useState(5);
  const [workerTotalPages, setWorkerTotalPages] = useState(1);
  const [allWorkers, setAllWorkers] = useState([]);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [address, setAddress] = useState("Fetching address...");
  const [suggestions, setSuggestions] = useState([]);
  const [mapRef, setMapRef] = useState(null);

  const addressRef = useRef(null);

  // Initialize when drawer opens
  useEffect(() => {
    if (open) {
      console.log("🚀 Drawer opened - Initializing...");
      setForm(initialForm);
      setAddress("Fetching address...");
      setCurrentLocation(null);
      setWorkerPage(1);
      setAllWorkers([]);
      setSuggestions([]);
      setSearchQuery("");

      fetchAllWorkers();

      // Get current location
      if (navigator.geolocation) {
        console.log("📍 Requesting geolocation...");
        getPositionWithFallback(
          (pos) => {
            const coords = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            };
            console.log("✅ Location obtained:", coords);
            setCurrentLocation(coords);
            fetchAddress(coords);
          },
          (error) => {
            console.error("❌ Geolocation error:", error);
            setLocationModalOpen(true);
          }
        );
      } else {
        console.error("❌ Geolocation not supported");
        setLocationModalOpen(true);
      }
    }
  }, [open]);

  // Fetch all workers
  const fetchAllWorkers = async () => {
    let page = 1;
    let allFetchedWorkers = [];
    let hasMore = true;

    console.log("👥 Fetching workers...");

    try {
      while (hasMore) {
        const res = await fetch(
          `${BASE_URL}/public/workers?company_id=${companyId}&page=${page}&limit=${workerLimit}&status=active`,
          {
            headers: { Authorization: `Bearer ${decryptedToken}` },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch workers");
        const data = await res.json();
        const pageWorkers = (data?.data?.records || []).map((w) => ({
          label: `${w.first_name} ${w.last_name} (${w.email})`,
          value: w.id,
        }));
        allFetchedWorkers = [...allFetchedWorkers, ...pageWorkers];

        const totalPages = data?.data?.pagination?.pages || 1;
        setWorkerTotalPages(totalPages);

        if (page >= totalPages) {
          hasMore = false;
        } else {
          page++;
        }
      }
      allFetchedWorkers.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );
      console.log("✅ Workers fetched:", allFetchedWorkers.length);
      setWorkers(allFetchedWorkers);
      setAllWorkers(allFetchedWorkers);
    } catch (err) {
      console.error("❌ Error fetching workers:", err);
    }
  };



useEffect(() => {
  if (open && !hasCheckedPermission) {
    setHasCheckedPermission(true);
    console.log("🚀 Drawer opened - Checking permission...");

    // Check if geolocation permission is granted on mount
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'denied') {
          console.log("❌ Permission denied");
          setLocationModalOpen(true);
        } else if (result.state === 'granted') {
          console.log("✅ Permission granted");
          handleGetCurrentLocation();
        } else {
          // 'prompt' state - will ask when getCurrentLocation is called
          console.log("❓ Permission prompt");
          handleGetCurrentLocation();
        }
      });
    } else {
      // Fallback for browsers that don't support permissions API
      handleGetCurrentLocation();
    }
  }

  // Reset permission check when drawer closes
  if (!open) {
    setHasCheckedPermission(false);
  }
}, [open, hasCheckedPermission]);
// Replace the geolocation.getCurrentPosition call with this new function
const handleGetCurrentLocation = () => {
  if (!navigator.geolocation) {
    console.error("❌ Geolocation not supported");
    toast.error(t("geolocationNotSupported") || "Geolocation not supported");
    setLocationModalOpen(true);
    return;
  }

  // Check internet connectivity
  if (!isOnline()) {
    console.error("❌ No internet connection");
    toast.error(t("locationInternetError") || "No internet connection. Please check your connection.");
    setLocationModalOpen(true);
    return;
  }

  console.log("📍 Requesting geolocation...");
  getPositionWithFallback(
    (pos) => {
      const coords = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
      };
      console.log("✅ Location obtained:", coords);
      setCurrentLocation(coords);
      fetchAddress(coords);
      setLocationModalOpen(false); // Close modal on success
    },
    (error) => {
      console.error("❌ Geolocation error:", error);

      if (error.code === error.PERMISSION_DENIED) {
        console.log("❌ Permission denied by user");
        setLocationModalOpen(true);
      } else if (error.code === error.TIMEOUT) {
        toast.error(t("locationTimeout") || "Location request timed out");
      } else {
        toast.error(t("unableToFetchLocation") || "Unable to fetch location");
      }
    }
  );
};

  // Fetch address from coordinates using Google Geocoding
// Update fetchAddress to handle internet check
const fetchAddress = (coords) => {
  if (!window.google || !coords) {
    console.log("⚠️ Google Maps not loaded or no coords");
    return;
  }

  // Check internet before fetching
  if (!isOnline()) {
    console.error("❌ No internet connection");
    toast.error(t("locationInternetError") || "No internet connection");
    setAddress("No internet connection");
    return;
  }

  console.log("🗺️ Fetching address for:", coords);
  const geocoder = new window.google.maps.Geocoder();

  geocoder.geocode({ location: coords }, (results, status) => {
    console.log("🗺️ Geocoding status:", status);

    if (status === "OK" && results[0]) {
      const formattedAddress = results[0].formatted_address;
      setAddress(formattedAddress);

      if (addressRef.current) {
        addressRef.current.value = formattedAddress;
      }

      console.log("✅ Address fetched:", formattedAddress);
    } else {
      if (!isOnline()) {
        console.error("❌ No internet connection");
        setAddress("No internet connection");
      } else {
        console.error("❌ Geocoding failed:", status);
        setAddress("Address not found");
      }
    }
  });
};

useEffect(() => {
  if (!searchQuery.trim() || !window.google) {
    setSuggestions([]);
    return;
  }

  // Check internet before searching
  if (!isOnline()) {
    console.log("⚠️ No internet - skipping search");
    setSuggestions([]);
    return;
  }

  console.log("🔍 Searching for:", searchQuery);

  const service = new window.google.maps.places.AutocompleteService();

  const request = {
    input: searchQuery,
    types: ["geocode", "establishment"],
  };

  service.getPlacePredictions(request, (predictions, status) => {
    console.log("🔍 Predictions status:", status);

    if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
      setSuggestions(predictions);
    } else {
      setSuggestions([]);
    }
  });
}, [searchQuery]);

  // Handle search suggestion selection
const handleSearchSelect = (suggestion) => {
  if (!window.google) return;

  // Check internet before searching
  if (!isOnline()) {
    toast.error(t("locationInternetError") || "No internet connection");
    return;
  }

  console.log("📍 Selected suggestion:", suggestion);
  const geocoder = new window.google.maps.Geocoder();

  geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
    console.log("📍 Place geocoding status:", status);

    if (status === "OK" && results[0]) {
      const location = results[0].geometry.location;

      const newPos = {
        lat: location.lat(),
        lng: location.lng(),
      };

      console.log("✅ Selected location:", newPos);

      setCurrentLocation(newPos);
      fetchAddress(newPos);
      setSearchQuery(results[0].formatted_address);
      setSuggestions([]);
      setSearchOpen(false);

      // Pan map to new location
      if (mapRef) {
        mapRef.panTo(newPos);
        mapRef.setZoom(17);
      }
    }
  });
};




  // Reflect browser location allow/block changes live (no refresh needed).
  useLocationPermissionSync({
    enabled: open,
    onGranted: () => {
      if (locationModalOpen || !currentLocation) {
        setLocationModalOpen(false);
        handleGetCurrentLocation();
      }
    },
    onDenied: () => setLocationModalOpen(true),
  });
  useEffect(() => {
  const handleOnline = () => {
    toast.success(t("internetRestored") || "Internet connection restored");
    // Retry fetching address if coordinates exist
    if (currentLocation?.lat && currentLocation?.lng) {
      fetchAddress(currentLocation);
    }
  };

  const handleOffline = () => {
    toast.error(t("internetConnectionLost") || "Internet connection lost. Please check your network.");
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}, [currentLocation]);
  // Handle marker drag
  const onMarkerDragEnd = (e) => {
    const newPos = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };

    console.log("🔄 Marker dragged to:", newPos);
    setCurrentLocation(newPos);
    fetchAddress(newPos);
  };

  const toggleDay = (day) => {
    setForm((prev) => {
      const updated = prev.workingDays.includes(day)
        ? prev.workingDays.filter((d) => d !== day)
        : [...prev.workingDays, day];
      console.log("📅 Working days updated:", updated);
      return { ...prev, workingDays: updated };
    });
  };

  const handleChange = (e) => {
    console.log(`📝 Field changed: ${e.target.name} = ${e.target.value}`);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const convertLocalTimeToUTCTimeOnly = (timeStr) => {
    if (!timeStr) return "";

    const utcISO = convertLocalDateTimeToUTC(timeStr);
    if (!utcISO) return "";

    const utcTime = new Date(utcISO).toISOString().slice(11, 16);
    console.log(
      "🕒 [ADD][LOCAL->UTC] Local input time converted for API storage",
      {
        localInput: timeStr,
        utcISO,
        utcTimeOnlyForPayload: utcTime,
      }
    );
    return utcTime;
  };

  // Submit handler
  const handleSubmit = async () => {
    console.log("==================================");
    console.log("🚀 SCHEDULE SUBMISSION STARTED");
    console.log("==================================");
    console.log("📋 Form data:", form);

    if (!form.worker) {
      toast.error(t("editModal.validation.selectWorker"));
      console.log("❌ No worker selected");
      return;
    }

    // Validate times
    if (form.startTime && form.endTime && form.endTime <= form.startTime) {
      toast.error(t("editModal.validation.shiftEndAfterStart"));
      console.log("❌ Invalid shift times");
      return;
    }

    // Validate location
    if (!currentLocation) {
      console.log("❌ No location available");
      setLocationModalOpen(true);
      return;
    }

    const payload = {
      schedule: {
        days: form.workingDays,
        start: convertLocalTimeToUTCTimeOnly(form.startTime),
        end: convertLocalTimeToUTCTimeOnly(form.endTime),
        breakPolicy: { unpaid: Number(form.breakMinutes) || 0 },
        graceMinutes: Number(form.graceMinutes) || 0,
      },
      location: {
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        radiusMeters: Number(form.radiusMeters) || 100,
        locationName: form.locationName,
      },
    };

    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log("🌍 [ADD] Browser timezone detected:", browserTimezone);
    console.log("📤 [ADD] Payload being sent (UTC schedule values):", JSON.stringify(payload, null, 2));

    setLoading(true);
    try {
      const url = `${BASE_URL}/company-admins/workers/${form.worker}/attendance-settings`;
      console.log("📤 POST to:", url);

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(decryptedToken ? { Authorization: `Bearer ${decryptedToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const responseData = await res.json();
      console.log("📥 Response status:", res.status);
      console.log("📥 Response data:", responseData);

      const lang = i18n.language?.split("-")[0] || "en";
      const apiMessage =
        (lang === "es" && responseData.message_es) ||
        responseData.message_en ||
        responseData.message;

      if (!res.ok) {
        toast.error(apiMessage || t("editModal.toast.apiFailed", { status: res.status }));
        throw new Error(`Failed: ${res.status}`);
      }

      console.log("✅ Schedule created successfully");
      toast.success(apiMessage || t("editModal.toast.scheduleCreated"));
      onAddSuccess();
      onClose();
    } catch (err) {
      console.error("❌ Submission error:", err);
      toast.error(t("editModal.toast.addError", { message: err.message }));
    } finally {
      setLoading(false);
      console.log("==================================");
      console.log("🏁 SUBMISSION ENDED");
      console.log("==================================");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 560 } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E0E0E0",
          backgroundColor: "#f5f6f7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              color: "#172B4D",
            }}
          >
            {t("editModal.titleAdd")}
          </CustomText>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Worker Select */}
        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}
          >
            {t("taskAddModal.assigningWorkers")}
          </Typography>
          <MultiSelectDropdown
            style={{ zIndex: 9999 }}
            multiple={false}
            label=""
            name="worker"
            values={form.worker}
            options={workers}
            enableSearch={true}
            onChange={(e) => {
              console.log("👤 Worker selected:", e.target.value);
              setForm((prev) => ({ ...prev, worker: e.target.value }));
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <div>
                  <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                    {option.label.split(" (")[0]}
                  </span>
                  <div style={{ fontSize: "12px", color: "#6B6B6B" }}>
                    {option.label.match(/\((.*)\)/)?.[1]}
                  </div>
                </div>
              </li>
            )}
            renderValue={(selected) => {
              const worker = workers.find((w) => w.value === selected);
              return worker ? worker.label : "";
            }}
          />
        </Box>

        {/* Days Row */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3, gap: 1 }}>
          {fullDays.map((day) => {
            const short = day.slice(0, 3);
            const isSelected = form.workingDays.includes(short);
            return (
              <Box
                key={day}
                onClick={() => toggleDay(short)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  userSelect: "none",
                  bgcolor: isSelected ? "#1976d2" : "#e0e0e0",
                  color: isSelected ? "#fff" : "#000",
                  transition: "all 0.2s",
                }}
              >
                {day[0]}
              </Box>
            );
          })}
        </Box>

        {/* Map Section */}
        <Box sx={{ mb: 3 }}>
          {/* Search toggle */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <IconButton
              onClick={() => {
                const newState = !searchOpen;
                console.log("🔍 Search toggled:", newState);
                setSearchOpen(newState);
                if (!newState) {
                  setSuggestions([]);
                  setSearchQuery("");
                }
              }}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                width: 36,
                height: 36,
                "&:hover": { backgroundColor: "#115293" },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Search input with suggestions */}
          {searchOpen && (
            <Box sx={{ mb: 2, position: "relative" }}>
              <input
                type="text"
                placeholder={t("searchLocation") || "Search location..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "15px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  fontFamily: "Poppins, sans-serif",
                }}
              />

              {/* Suggestions dropdown */}
              {suggestions.length > 0 && (
                <Paper
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 10000,
                    mt: 0.5,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}
                >
                  <List>
                    {suggestions.map((sug) => (
                      <ListItem
                        key={sug.place_id}
                        button
                        onClick={() => handleSearchSelect(sug)}
                        sx={{
                          "&:hover": { backgroundColor: "#f0f0f0" },
                          cursor: "pointer",
                        }}
                      >
                        <ListItemText
                          primary={sug.structured_formatting.main_text}
                          secondary={sug.structured_formatting.secondary_text}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontFamily: "Poppins, sans-serif",
                          }}
                          secondaryTypographyProps={{
                            fontSize: "12px",
                            fontFamily: "Poppins, sans-serif",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}

          {/* Google Map */}
          {currentLocation && (
            <Box sx={{ height: 250, borderRadius: "8px", overflow: "hidden", mb: 1 }}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={currentLocation}
                zoom={17}
                onLoad={(map) => {
                  console.log("🗺️ Map loaded");
                  setMapRef(map);
                }}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker
                  position={currentLocation}
                  draggable={true}
                  onDragEnd={onMarkerDragEnd}
                />
                <Circle
                  center={currentLocation}
                  radius={Number(form.radiusMeters) || 100}
                  options={{
                    fillColor: "#1976d2",
                    fillOpacity: 0.2,
                    strokeColor: "#1976d2",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              </GoogleMap>
            </Box>
          )}

          {/* Address field */}
          <input
            ref={addressRef}
            type="text"
            readOnly
            value={address}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              backgroundColor: "#f4f4f4",
              fontFamily: "Poppins, sans-serif",
            }}
          />
        </Box>

        {/* Location Name */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
            {t("editModal.labels.locationName")}
          </Typography>
          <TextField
            type="text"
            name="locationName"
            value={form.locationName}
            onChange={handleChange}
            placeholder={t("addModal.labels.locationName") || "Location Name"}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                "& fieldset": { border: "2px solid #091E4224" },
                "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                "& input": {
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                },
              },
            }}
          />
        </Box>

        {/* Radius */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
            {t("editModal.labels.locationRadius")}
          </Typography>
          <TextField
            type="number"
            name="radiusMeters"
            value={form.radiusMeters}
            onChange={handleChange}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                height: "50px",
                "& fieldset": { border: "2px solid #091E4224" },
                "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                "& input": {
                  fontSize: "13px",
                  fontFamily: "Poppins, sans-serif",
                },
              },
            }}
          />
        </Box>

        {/* Shift Times */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
              {t("editModal.labels.shiftStart")}
            </Typography>
            <TextField
              type="time"
              name="startTime"
              value={form.startTime}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
              {t("editModal.labels.shiftEnd")}
            </Typography>
            <TextField
              type="time"
              name="endTime"
              value={form.endTime}
              onChange={handleChange}
              size="small"
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Grace + Break */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
              {t("editModal.labels.graceMinutes")}
            </Typography>
            <TextField
              type="number"
              name="graceMinutes"
              value={form.graceMinutes}
              onChange={handleChange}
              size="small"
              fullWidth
              inputProps={{
                min: 0,
                onKeyDown: (e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "15px", mb: 0.5, fontFamily: "Poppins, sans-serif" }}>
              {t("editModal.labels.breakMinutes")}
            </Typography>
            <TextField
              type="number"
              name="breakMinutes"
              value={form.breakMinutes}
              onChange={handleChange}
              size="small"
              fullWidth
              inputProps={{
                min: 0,
                onKeyDown: (e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E") {
                    e.preventDefault();
                  }
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  height: "50px",
                  "& fieldset": { border: "2px solid #091E4224" },
                  "&.Mui-focused fieldset": { border: "2px solid #006EC2" },
                  "& input": {
                    fontSize: "13px",
                    fontFamily: "Poppins, sans-serif",
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 2 }}>
          <CustomButton
            size="small"
            onClick={handleSubmit}
            disabled={loading}
            sx={{ px: 2, display: "flex", alignItems: "center" }}
            label={
              loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} sx={{ color: "white" }} />
                  {t("editModal.create")}
                </Box>
              ) : (
                t("editModal.create")
              )
            }
          />
        </Box>
      </Box>

      <LocationPermissionModal
        open={locationModalOpen}
        onCancel={() => setLocationModalOpen(false)}
      />
    </Drawer>
  );
};

export default AddScheduleDrawer;


